import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { useEffectReducer } from "use-effect-reducer";
import { authHeader, authHeaderFor, clearToken, getToken, setToken } from "./auth.ts";
import { reducer, initialState } from "./state/reducer.ts";
import type { AppState } from "./state/actions.ts";
import type { Tool } from "./types/document.ts";
import { Workspace } from "./components/workspace.tsx";
import { Toolbar } from "./components/toolbar.tsx";
import { ExportBar } from "./components/export-bar.tsx";
import { EmojiPicker } from "./components/emoji-picker.tsx";
import { SendForm } from "./components/send-form.tsx";
import { Mailbox } from "./components/mailbox.tsx";
import { ChatPanel } from "./components/chat-panel.tsx";
import { useAssistantChat } from "./hooks/use-assistant-chat.ts";
import { exportJson } from "./serialization/export-json.ts";
import { exportPng } from "./serialization/export-image.ts";
import type { TelegramDocument } from "./types/document.ts";

const STORAGE_KEY = "teddygram:document";

type View = "compose" | "mailbox";

interface User {
  id: number;
  username: string;
  displayName: string;
}

function loadSavedState(): AppState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return { ...initialState, ...saved, inProgress: null, drawStartPoint: null };
  } catch {
    return null;
  }
}

const KEY_TO_TOOL: Record<string, Tool> = {
  p: "pencil",
  l: "line",
  r: "rect",
  e: "ellipse",
  m: "emoji",
  t: "text",
};

export function App() {
  const stateRef = useRef<AppState>(initialState);
  const [view, setView] = useState<View>("compose");
  const [users, setUsers] = useState<User[]>([]);
  const [session, setSession] = useState<"loading" | "anon" | "authed">(() =>
    getToken() ? "loading" : "anon",
  );
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [tokenInput, setTokenInput] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);

  const [state, dispatch] = useEffectReducer(reducer, loadSavedState() ?? initialState, {
    exportJson: (state) => {
      exportJson(state as AppState);
    },
    exportPng: (state) => {
      exportPng(state as AppState);
    },
  });
  stateRef.current = state;

  const getDocument = useCallback((): TelegramDocument => {
    const s = stateRef.current;
    return {
      version: 1,
      text: { html: s.textHtml, style: s.textStyle },
      canvas: { width: s.canvasWidth, height: s.canvasHeight },
      objects: s.objects,
    };
  }, []);

  const {
    messages: chatMessages,
    isStreaming,
    sendMessage,
    cancel,
  } = useAssistantChat(dispatch, getDocument, session === "authed");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setSession("anon");
      return;
    }
    fetch("/api/me", { headers: authHeader() })
      .then((r) => {
        if (!r.ok) {
          clearToken();
          setSession("anon");
          return null;
        }
        return r.json() as Promise<User>;
      })
      .then((u) => {
        if (u) {
          setCurrentUser(u);
          setSession("authed");
        }
      })
      .catch(() => {
        clearToken();
        setSession("anon");
      });
  }, []);

  useEffect(() => {
    if (session !== "authed") return;
    fetch("/api/users", { headers: authHeader() })
      .then((r) => r.json())
      .then((data: User[]) => setUsers(data))
      .catch(() => {});
  }, [session]);

  useEffect(() => {
    const save = () => {
      const {
        undoStack: _u,
        redoStack: _r,
        inProgress: _i,
        drawStartPoint: _d,
        ...rest
      } = stateRef.current;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
    };
    const id = setInterval(save, 2000);
    window.addEventListener("beforeunload", save);
    return () => {
      clearInterval(id);
      window.removeEventListener("beforeunload", save);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (view !== "compose") return;
      const inText = (e.target as HTMLElement)?.isContentEditable;

      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "Z") {
        e.preventDefault();
        dispatch({ type: "REDO" });
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        dispatch({ type: "UNDO" });
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        dispatch({ type: "EXPORT_JSON" });
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "E") {
        e.preventDefault();
        dispatch({ type: "EXPORT_PNG" });
        return;
      }

      if (inText) return;
      const tool = KEY_TO_TOOL[e.key.toLowerCase()];
      if (tool) dispatch({ type: "SET_TOOL", tool });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch, view]);

  const tryLogin = async () => {
    const t = tokenInput.trim();
    if (!t) return;
    const res = await fetch("/api/me", { headers: authHeaderFor(t) });
    if (!res.ok) {
      setLoginError("Invalid token");
      return;
    }
    const u = (await res.json()) as User;
    setToken(t);
    setCurrentUser(u);
    setSession("authed");
    setLoginError(null);
    setTokenInput("");
  };

  const signOut = () => {
    clearToken();
    setCurrentUser(null);
    setUsers([]);
    setSession("anon");
  };

  if (session === "loading") {
    return (
      <div
        class="flex flex-col items-center justify-center min-h-screen p-4"
        style={{ background: "#FFF3D6", color: "#5D4E37" }}
      >
        Loading...
      </div>
    );
  }

  if (session === "anon") {
    return (
      <div
        class="flex flex-col items-center justify-center gap-4 min-h-screen p-4"
        style={{ background: "#FFF3D6" }}
      >
        <label class="flex flex-col gap-2 text-sm" style={{ color: "#5D4E37" }}>
          Username
          <input
            class="rounded px-3 py-2 min-w-[280px]"
            style={{ background: "#FFF8E7", border: "1px solid #D4B078", color: "#5D4E37" }}
            type="password"
            autocomplete="off"
            value={tokenInput}
            onInput={(e) => setTokenInput((e.target as HTMLInputElement).value)}
          />
        </label>
        {loginError && <span class="text-sm text-red-600">{loginError}</span>}
        <button
          type="button"
          class="px-4 py-2 rounded-lg text-sm"
          style={{ background: "#C48A3F", color: "#FFF8E7" }}
          onClick={() => void tryLogin()}
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div
      class="flex flex-col items-center gap-4 p-4 min-h-screen"
      style={{ background: "#FFF3D6" }}
    >
      <div class="flex items-center gap-4 w-full max-w-4xl">
        <div class="flex gap-1">
          <button
            class="px-3 py-1 rounded-lg text-sm transition-colors"
            style={
              view === "compose"
                ? { background: "#C48A3F", color: "#FFF8E7" }
                : { background: "#FFF8E7", border: "1px solid #D4B078", color: "#5D4E37" }
            }
            onClick={() => setView("compose")}
          >
            Compose
          </button>
          <button
            class="px-3 py-1 rounded-lg text-sm transition-colors"
            style={
              view === "mailbox"
                ? { background: "#C48A3F", color: "#FFF8E7" }
                : { background: "#FFF8E7", border: "1px solid #D4B078", color: "#5D4E37" }
            }
            onClick={() => setView("mailbox")}
          >
            Mailbox
          </button>
        </div>
        {currentUser && (
          <span class="text-sm ml-auto" style={{ color: "#8B7355" }}>
            Logged in as {currentUser.displayName}
            <button
              type="button"
              class="ml-3 text-xs underline"
              style={{ color: "#C48A3F" }}
              onClick={signOut}
            >
              Sign out
            </button>
          </span>
        )}
      </div>

      {view === "compose" && (
        <>
          <SendForm
            currentUserId={currentUser!.id}
            state={state}
            onSent={() => {
              dispatch({ type: "CLEAR_CANVAS" });
              setView("mailbox");
            }}
          />
          <Toolbar
            activeTool={state.activeTool}
            activeColor={state.activeColor}
            activeWidth={state.activeWidth}
            activeFill={state.activeFill}
            undoEnabled={state.undoStack.length > 0}
            redoEnabled={state.redoStack.length > 0}
            dispatch={dispatch}
          />
          <div class="flex gap-4 items-start">
            <Workspace
              objects={state.objects}
              inProgress={state.inProgress}
              canvasWidth={state.canvasWidth}
              canvasHeight={state.canvasHeight}
              activeTool={state.activeTool}
              activeEmoji={state.activeEmoji}
              activeEmojiSize={state.activeEmojiSize}
              textHtml={state.textHtml}
              textStyle={state.textStyle}
              dispatch={dispatch}
            />
            {state.activeTool === "emoji" && (
              <EmojiPicker
                activeEmoji={state.activeEmoji}
                activeEmojiSize={state.activeEmojiSize}
                dispatch={dispatch}
              />
            )}
            <ChatPanel
              messages={chatMessages}
              isStreaming={isStreaming}
              onSend={sendMessage}
              onCancel={cancel}
            />
          </div>
          <ExportBar dispatch={dispatch} />
        </>
      )}

      {view === "mailbox" && <Mailbox users={users} />}
    </div>
  );
}
